import React, { useEffect, useState } from 'react';

export default function EnergyCertificate({ estate = {} }) {
  const [windowWidth, setWindowWidth] = useState(0);
  
  // Energy classes array with precise thermal ranges for accurate mapping
  const energyClassData = [
    { letter: 'A+', minValue: 0, maxValue: 30, color: '#01853f' },
    { letter: 'A', minValue: 30, maxValue: 50, color: '#35a035' },
    { letter: 'B', minValue: 50, maxValue: 75, color: '#7fb143' },
    { letter: 'C', minValue: 75, maxValue: 100, color: '#fecb00' },
    { letter: 'D', minValue: 100, maxValue: 130, color: '#feb600' },
    { letter: 'E', minValue: 130, maxValue: 160, color: '#fd7400' },
    { letter: 'F', minValue: 160, maxValue: 200, color: '#e13120' },
    { letter: 'G', minValue: 200, maxValue: 250, color: '#c7271f' },
    { letter: 'H', minValue: 250, maxValue: 350, color: '#9c1319' }
  ];

  // Get window width to calculate relative positions
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    // Initial width
    handleResize();
    
    // Add resize listener
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Extract values with safe fallbacks
  const energyClass = estate?.energy_efficiency_class?.value || null;
  const thermalValue = parseFloat(estate?.thermal_characteristic?.value) || 0;

  // Helper function to map thermal value to exact position percentage (0-100)
  const mapThermalValueToPosition = () => {
    // If no energy class or thermal value, return null
    if (!energyClass || thermalValue <= 0) return null;
    
    // Find the energy class data
    const classData = energyClassData.find(c => c.letter === energyClass);
    if (!classData) return null;
    
    // Find range in overall scale
    const minOverall = energyClassData[0].minValue;
    const maxOverall = energyClassData[energyClassData.length - 1].maxValue;
    const totalRange = maxOverall - minOverall;
    
    // Calculate position based on thermal value relative to overall range
    let position = ((thermalValue - minOverall) / totalRange) * 100;
    
    // Clamp position to valid range (3-97%)
    return Math.max(3, Math.min(97, position));
  };
  
  const positionPercent = mapThermalValueToPosition();
  
  // Helper function to render detail rows
  const renderDetail = (label, value) => {
    if (!value) return null;
    
    return (
      <div className="flex flex-col sm:flex-row py-2">
        <div className="w-full  sm:w-1/2 text-gray-600 mb-1 sm:mb-0">{label}</div>
        <div className="w-full sm:w-1/2 font-medium">{value}</div>
      </div>
    );
  };

  return (
    <div className="p-4 rounded-lg bg-prime-50">
      <h2 className="text-2xl font-medium text-[#e95c0c] mb-6">Energieausweis</h2>
      
      {/* Energy Efficiency Scale */}
      <div className="relative w-full my-14">
        {/* Colored scale bar */}
        <div className="relative w-full h-12 bg-gradient-to-r from-green-500 via-yellow-400 to-red-500 rounded-sm">
          {/* Class dividers and labels */}
          {energyClassData.map((classInfo, index) => {
            // Calculate position percentage based on thermal range
            const minOverall = energyClassData[0].minValue;
            const maxOverall = energyClassData[energyClassData.length - 1].maxValue;
            const posPercent = ((classInfo.minValue - minOverall) / (maxOverall - minOverall)) * 100;
            
            return (
              <div 
                key={classInfo.letter}
                className="absolute top-0 bottom-0"
                style={{ left: `${posPercent}%` }}
              >
                {/* Vertical divider line */}
                {index > 0 && (
                  <div className="absolute top-0 bottom-0 w-px bg-white opacity-70"></div>
                )}
                
                {/* Class letter */}
                <div className="absolute top-full mt-2 transform -translate-x-1/2 text-sm font-bold">
                  {classInfo.letter}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Value indicator */}
        {positionPercent !== null && (
          <div 
            className="absolute transform -translate-x-1/2"
            style={{ 
              left: `${positionPercent}%`,
              top: '-24px'
            }}
          >
            <div className="flex flex-col items-center">
              <div className="bg-prime-900 text-white px-2 py-1 rounded whitespace-nowrap">
                {thermalValue} kWh/(m²·a)
              </div>
              <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-t-prime-900 border-l-transparent border-r-transparent"></div>
            </div>
          </div>
        )}
      </div>
      
      {/* Details */}
      <div className="grid md:grid-cols-2 gap-4 mt-8">
        <div>
          {renderDetail('Energieausweistyp', estate?.building_energy_rating_type?.value)}
          {renderDetail('Effizienzklasse', estate?.energy_efficiency_class?.value)}
          {renderDetail('Endenergiebedarf', thermalValue ? `${thermalValue} kWh/(m²·a)` : null)}
        </div>
        <div>
          {renderDetail('Wesentlicher Energieträger', estate?.firing_types?.value)}
          {renderDetail('Heizungsart', estate?.heating_type?.value)}
          {renderDetail('Gültig bis', estate?.energy_certificate_end_date?.value)}
        </div>
      </div>
      
      {/* Empty state */}
      {!energyClass && (
        <div className="mt-4 p-3 bg-gray-100 rounded text-gray-600 text-center">
          Keine Energieeffizienzklasse verfügbar
        </div>
      )}
    </div>
  );
}